import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import Box from 'ui-box';
import { majorScale } from '.';
import { Card } from './Card';
import { Icon } from './Icon';
import { Button } from './Button';
import { getColorFromIntent, Intents } from './Intents';
import { Portal } from './Portal';

import styles from './Toaster.module.scss';
import { Transition } from 'react-spring/renderprops';
import { useIntl } from 'react-intl';

export interface IToastProps {
  message: string;
  intent?: Intents;
  actionMessage?: string;
  onAction?: () => void;
  onClose?: () => void;
  timeout?: number;
}

export const Toast: React.FC<IToastProps & { id: number }> = ({
  id,
  message,
  intent = 'info',
  actionMessage,
  timeout = 4000,
  onAction,
  onClose,
}) => {
  const [show, setShow] = useState(true);
  const [hovered, setHovered] = useState(false);

  const { removeToast } = useToast();

  useEffect(() => {
    if (hovered) return;
    const timer = setTimeout(() => {
      setShow(false);
    }, timeout);
    return () => {
      clearTimeout(timer);
    };
  }, [id, timeout, hovered]);

  return (
    <Transition
      items={show}
      from={{ opacity: 0, transform: 'translateY(-16px)' }}
      enter={{ opacity: 1, transform: 'translateY(0)' }}
      leave={{ opacity: 0, transform: 'translateY(-16px)' }}
      onDestroyed={() => removeToast(id)}
    >
      {(show) =>
        show &&
        ((props) => (
          <Card
            elevation={2}
            className={styles.toast}
            style={props}
            onMouseOver={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
          >
            <Box padding={majorScale(1)} paddingBottom={0} paddingTop={2}>
              <Icon
                icon={
                  intent === 'info'
                    ? 'info-sign'
                    : intent === 'success'
                    ? 'tick-circle'
                    : intent === 'warning'
                    ? 'warning-sign'
                    : intent === 'danger'
                    ? 'error'
                    : 'dot'
                }
                size={majorScale(2)}
                color={getColorFromIntent(intent)}
              />
            </Box>
            <Box marginTop={6}>
              <p className="medium">{message}</p>
              {actionMessage && onAction && (
                <Button
                  minimal
                  small
                  intent={intent}
                  fontWeight={600}
                  marginX={majorScale(-1)}
                  marginBottom={majorScale(0.5)}
                  onClick={() => {
                    setShow(false);
                    onAction();
                  }}
                >
                  {actionMessage}
                </Button>
              )}
            </Box>
            <Box>
              <Button
                minimal
                iconLeft="cross"
                onClick={() => {
                  setShow(false);
                  onClose && onClose();
                }}
              />
            </Box>
          </Card>
        ))
      }
    </Transition>
  );
};

const ToastContainer = ({ toasts }: { toasts: IToast[] }) => {
  return (
    <Portal className={styles.portal}>
      {toasts.map((toast) => (
        <Toast key={toast.id} id={toast.id} {...toast.props} />
      ))}
    </Portal>
  );
};

let toastId = 0;

const ToastContext =
  React.createContext<{
    addToast: (toast: IToastProps) => number;
    removeToast: (id: number) => void;
    showDefaultError: (error?: any) => void;
  } | null>(null);

interface IToast {
  id: number;
  props: IToastProps;
}

export const ToastProvider = ({ children }: { children: ReactNode }) => {
  const [toasts, setToasts] = useState<IToast[]>([]);
  const intl = useIntl();
  const addToast = useCallback(
    (props: IToastProps) => {
      const id = toastId++;
      setToasts((toasts) => [...toasts, { id, props }]);
      return id;
    },
    [setToasts]
  );

  const removeToast = useCallback(
    (id) => {
      // We need to wait till next event loop, as the toast would otherwise be removed prematurely and raise a react exception.
      setImmediate(() =>
        setToasts((toasts) => toasts.filter((t) => t.id !== id))
      );
    },
    [setToasts]
  );

  const showDefaultError = useCallback(
    (error?: any) => {
      let message = intl.formatMessage({
        id: 'defaults.error',
        defaultMessage: 'An error occured. Please try again',
      });
      if (error && error.response && error.response.data) {
        const data = error.response.data;
        if (data.length) {
          if (data.join) message = data.join('. ');
          else message = data;
        } else {
          message = Object.keys(data)
            .map((key) => data[key])
            .join('. ');
        }
      }
      addToast({
        message,
        intent: 'danger',
      });
    },
    [addToast, intl]
  );

  return (
    <ToastContext.Provider value={{ addToast, removeToast, showDefaultError }}>
      <ToastContainer toasts={toasts} />
      {children}
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const helpers = useContext(ToastContext);
  if (helpers === null) {
    throw new TypeError(
      'Toaster not setup. Please ensure you use the ToastProvider to create a context.'
    );
  }
  return helpers!;
};
